import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';

const StatCard = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm flex items-center gap-4">
    <div className={`p-3 rounded-lg ${color}`}>
      <SafeIcon name={icon} className="w-6 h-6" />
    </div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{title}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const Dashboard = () => {
  const { data, loading, t } = useAppContext();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  if (loading) return <div className="p-8 text-center text-gray-500">{t('dashboard.loading')}</div>;

  const targetDateStr = selectedDate;
  const todaysOrders = (data.orders || []).filter(o => {
    const d = o.delivery_date ? String(o.delivery_date).split('T')[0] : '';
    return d === targetDateStr;
  });

  const todaysOrderIds = todaysOrders.map(o => String(o.id));
  const todaysLines = (data.orderLines || []).filter(ol => todaysOrderIds.includes(String(ol.order_id)));

  const totalItems = todaysLines.reduce((sum, line) => sum + Number(line.quantity), 0);

  const categoryTotals = { Bread: 0, Cakes: 0, Pastry: 0, 'Bløddej': 0 };
  todaysLines.forEach(line => {
    const product = data.products.find(p => String(p.id) === String(line.product_id));
    if (product && categoryTotals[product.category] !== undefined) {
      categoryTotals[product.category] += Number(line.quantity);
    }
  });

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-500 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
          <SafeIcon name="Calendar" className="text-gray-400 w-4 h-4" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border-none bg-transparent outline-none text-gray-700 font-medium cursor-pointer"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title={t('dashboard.totalOrders')} value={todaysOrders.length} icon="ShoppingCart" color="bg-blue-100 text-blue-600" />
        <StatCard title={t('dashboard.totalItems')} value={totalItems} icon="Activity" color="bg-green-100 text-green-600" />
        <StatCard title={t('dashboard.activeProducts')} value={data.products.filter(p => p.active).length} icon="Box" color="bg-amber-100 text-amber-600" />
        <StatCard title={t('dashboard.activeCustomers')} value={data.customers.filter(c => c.active).length} icon="Users" color="bg-purple-100 text-purple-600" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mt-8">
        <h2 className="text-lg font-bold text-gray-800 mb-6">{t('dashboard.breakdown')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(categoryTotals).map(([cat, amount]) => (
            <div key={cat} className="p-4 rounded-lg bg-gray-50 border border-gray-100 text-center">
              <p className="text-sm font-medium text-gray-500 mb-1">{cat}</p>
              <p className="text-3xl font-bold text-gray-800">{amount}</p>
              <p className="text-xs text-gray-400 mt-1">{t('dashboard.unitsOrdered')}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;